// The "making it" part of /mymaker (pages/MyMaker.js): the pencil turns
// into a tool - a brush, a camera, scissors... - and works on the maker's
// canvas. Each way of making is a short list of things happening to the
// canvas, in order, over one playing (t from 0 to 1):
//
//   { d, from, to, cls }   a line drawn on the canvas between `from` and
//                          `to`; the tool follows its tip
//   { at, pos, el }        something put on the canvas at `at` - a piece of
//                          paper, a ticket, a strip of photo; the tool moves
//                          to `pos` just before
//
// Everything is in the canvas's own units: 0 to CANVAS_SIZE across and down.

export const CANVAS_SIZE = 150;

// Seeded, so a torn edge is the same every time.
function random(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function torn(x, y, w, h, seed) {
  const r = random(seed);
  const pts = [];
  const edge = (x1, y1, x2, y2, n) => {
    for (let i = 0; i < n; i += 1) {
      const t = i / n;
      const jx = y1 === y2 ? 0 : (r() - 0.5) * 4;
      const jy = x1 === x2 ? 0 : (r() - 0.5) * 4;
      pts.push(`${(x + x1 + (x2 - x1) * t + jx).toFixed(1)},${(y + y1 + (y2 - y1) * t + jy).toFixed(1)}`);
    }
  };
  edge(0, 0, w, 0, 8);
  edge(w, 0, w, h, 6);
  edge(w, h, 0, h, 8);
  edge(0, h, 0, 0, 6);
  return pts.join(' ');
}

const Ticket = ({ x, y, rot = -6, label }) => (
  <g transform={`translate(${x} ${y}) rotate(${rot} 40 20)`}>
    <rect className="m2c-card" width="80" height="40" />
    <path className="m2c-thin" d="M60 3 V37" strokeDasharray="3 3" />
    <path className="m2c-thin" d="M10 28 H44 M10 33 H34" />
    <text className="m2c-ticket" x="10" y="17">
      {label}
    </text>
  </g>
);

// A strip of a photo: a slice of sky, sun and hill.
function Strip({ i, x }) {
  const id = `m2c-strip-${i}`;
  return (
    <g>
      <clipPath id={id}>
        <rect x={x} y="30" width="26" height="90" />
      </clipPath>
      <g clipPath={`url(#${id})`} transform={`translate(${(i - 1.5) * 4} 0)`}>
        <rect className="m2c-sky" x="18" y="30" width="114" height="90" />
        <circle className="m2c-sun" cx="104" cy="54" r="11" />
        <path className="m2c-hill" d="M18 120 V96 C44 76 66 96 88 86 C108 78 122 90 132 86 V120 Z" />
      </g>
    </g>
  );
}

const zigzag = (x, y, n, dx, dy) => `M${x} ${y} ${`l${dx} ${-dy} l${dx} ${dy} `.repeat(n)}`;

export const WAYS = {
  paint: {
    tool: 'brush',
    parts: [
      { d: 'M16 46 C44 20 96 64 134 36', from: 0, to: 0.38, cls: 'm2c-paint' },
      { d: 'M18 100 C52 74 96 124 134 96', from: 0.44, to: 0.82, cls: 'm2c-paint' },
      { d: 'M28 128 C60 120 90 132 122 124', from: 0.86, to: 1, cls: 'm2c-ink' },
    ],
  },
  write: {
    tool: 'nib',
    parts: [
      { d: `M18 52 ${'c4 -14 10 -14 14 0 c4 12 10 12 14 0 '.repeat(4)}`, from: 0, to: 0.3, cls: 'm2c-ink' },
      { d: `M18 82 ${'c4 -14 10 -14 14 0 c4 12 10 12 14 0 '.repeat(3)}`, from: 0.36, to: 0.6, cls: 'm2c-ink' },
      { d: `M18 112 ${'c4 -14 10 -14 14 0 c4 12 10 12 14 0 '.repeat(2)}`, from: 0.66, to: 0.84, cls: 'm2c-ink' },
      { d: 'M18 126 C46 122 70 130 92 124', from: 0.88, to: 1, cls: 'm2c-ink' },
    ],
  },
  collage: {
    tool: 'scissors',
    parts: [
      { d: `M12 64 ${'l10 -5 l10 5 '.repeat(6)}`, from: 0, to: 0.3, cls: 'm2c-thin' },
      { at: 0.42, pos: [48, 44], el: <polygon className="m2c-paper m2c-paper--deep" points={torn(14, 22, 72, 48, 3)} /> },
      { at: 0.62, pos: [100, 84], el: <polygon className="m2c-paper m2c-paper--tint" points={torn(64, 52, 70, 64, 11)} /> },
      { at: 0.84, pos: [56, 112], el: <polygon className="m2c-paper m2c-paper--wash" points={torn(22, 90, 70, 42, 23)} /> },
    ],
  },
  glue: {
    tool: 'glue',
    parts: [
      { d: 'M36 72 c10 -10 18 10 28 0 c10 -10 18 10 28 0 c10 -10 18 10 28 0', from: 0, to: 0.45, cls: 'm2c-glue' },
      { at: 0.7, pos: [75, 72], el: (label) => <Ticket x={35} y={52} label={label} /> },
    ],
  },
  photo: {
    tool: 'camera',
    parts: [
      { at: 0.22, pos: [75, 20], el: <circle className="m2c-flash" cx="75" cy="75" r="70" /> },
      { at: 0.42, pos: [31, 40], el: <Strip i={0} x={18} /> },
      { at: 0.58, pos: [59, 40], el: <Strip i={1} x={46} /> },
      { at: 0.86, pos: [115, 40], el: <Strip i={3} x={102} /> },
    ],
  },
  stitch: {
    tool: 'needle',
    // Stitches climbing from one corner to the other: a route from door to door.
    parts: [{ d: `M16 126 ${'l12 -24 l8 12 '.repeat(6)}`, from: 0, to: 1, cls: 'm2c-thread' }],
  },
  draw: {
    tool: 'pencil',
    parts: [
      { d: 'M30 122 V72 L75 38 L120 72 V122 Z', from: 0, to: 0.5, cls: 'm2c-ink' },
      { d: 'M66 122 V96 H84 V122', from: 0.55, to: 0.72, cls: 'm2c-ink' },
      { d: 'M40 80 H56 V96 H40 Z', from: 0.76, to: 0.88, cls: 'm2c-ink' },
      { d: 'M118 22 A10 10 0 1 1 117.9 22', from: 0.9, to: 1, cls: 'm2c-ink' },
    ],
  },
  mix: {
    tool: 'palette',
    parts: [
      { d: 'M14 54 C50 28 100 80 136 48', from: 0, to: 0.34, cls: 'm2c-paint' },
      { at: 0.52, pos: [80, 96], el: (label) => <Ticket x={44} y={78} rot={5} label={label} /> },
      { d: zigzag(18, 136, 5, 8, 12), from: 0.62, to: 0.95, cls: 'm2c-thread' },
    ],
  },
};

/** The ways of making, in the order the menu shows them. */
export const MAKE_WAYS = Object.keys(WAYS);

/** What a way of making has done to the canvas at `t` (0 to 1). `refs`
 *  collects the drawn lines, so the page can put the tool on their tip. */
export function CanvasWork({ way, t, label, refs }) {
  const def = WAYS[way];
  if (!def) return null;
  return def.parts.map((part, i) => {
    if (part.d) {
      const local = Math.min(1, Math.max(0, (t - part.from) / (part.to - part.from)));
      return (
        <path
          key={i}
          ref={refs ? (el) => (refs.current[i] = el) : undefined}
          className={part.cls}
          d={part.d}
          pathLength="1"
          // Hidden until it starts: a line with round ends still shows a dot
          // at full length 0.
          style={{ strokeDasharray: '1 1', strokeDashoffset: 1 - local, opacity: local > 0 ? undefined : 0 }}
        />
      );
    }
    if (t < part.at) return null;
    return (
      <g key={i} className="m2c-put">
        {typeof part.el === 'function' ? part.el(label) : part.el}
      </g>
    );
  });
}

/** Where the tool is at `t`: on the tip of the line being drawn, or over the
 *  spot where something is about to be put down. Null when nothing is
 *  happening at that moment. */
export function toolAt(way, t, refs) {
  const def = WAYS[way];
  if (!def) return null;
  for (let i = 0; i < def.parts.length; i += 1) {
    const part = def.parts[i];
    if (part.d && t >= part.from && t <= part.to) {
      const el = refs.current[i];
      if (!el) return null;
      const p = el.getPointAtLength(el.getTotalLength() * ((t - part.from) / (part.to - part.from)));
      return [p.x, p.y];
    }
    if (!part.d && t >= part.at - 0.16 && t <= part.at + 0.04) return part.pos;
  }
  return null;
}

// ---------------------------------------------------------------------------
// The tools. Each is drawn with its working end at (0, 0) - the brush's
// bristles, the nib's tip, the scissors' blades - the way the pencil is.
// ---------------------------------------------------------------------------

const TOOLS = {
  pencil: (
    <g transform="rotate(28)">
      <path className="m2t-wood" d="M0 0 L-6 -16 L6 -16 Z" />
      <path className="m2t-ink" d="M0 0 L-2.2 -6 L2.2 -6 Z" />
      <rect className="m2t-body" x="-6" y="-74" width="12" height="58" />
      <path className="m2t-line" d="M-6 -66 H6 M0 -66 V-16" />
      <rect className="m2t-body" x="-6" y="-84" width="12" height="10" rx="3" />
    </g>
  ),
  brush: (
    <g transform="rotate(28)">
      <path className="m2t-ink" d="M0 0 C-6 -4 -7 -12 -6 -18 L6 -18 C7 -12 6 -4 0 0 Z" />
      <rect className="m2t-body" x="-6" y="-30" width="12" height="12" />
      <path className="m2t-body" d="M-5 -30 L-2.5 -92 H2.5 L5 -30 Z" />
    </g>
  ),
  nib: (
    <g transform="rotate(28)">
      <path className="m2t-body" d="M0 0 L-7 -16 L-7 -24 L7 -24 L7 -16 Z" />
      <path className="m2t-line" d="M0 0 V-14" />
      <circle className="m2t-ink" cx="0" cy="-14" r="1.6" />
      <rect className="m2t-body" x="-8" y="-84" width="16" height="60" rx="4" />
    </g>
  ),
  scissors: (
    <g transform="rotate(-20)">
      <path className="m2t-line" d="M-4 0 L2 -36 M6 0 L-2 -36" />
      <circle className="m2t-body" cx="-9" cy="-46" r="9" />
      <circle className="m2t-body" cx="9" cy="-46" r="9" />
      <circle className="m2t-ink" cx="0" cy="-34" r="1.8" />
    </g>
  ),
  glue: (
    <g transform="rotate(24)">
      <path className="m2t-ink" d="M0 0 L-4 -12 L4 -12 Z" />
      <rect className="m2t-body" x="-5" y="-18" width="10" height="6" />
      <rect className="m2t-body" x="-15" y="-62" width="30" height="44" rx="5" />
      <path className="m2t-line" d="M-9 -46 H9 M-9 -38 H5" />
    </g>
  ),
  camera: (
    <g transform="translate(0 -8)">
      <rect className="m2t-body" x="-24" y="-30" width="48" height="30" rx="4" />
      <path className="m2t-body" d="M-8 -30 L-4 -38 H8 L12 -30 Z" />
      <circle className="m2t-body" cx="0" cy="-15" r="10" />
      <circle className="m2t-ink" cx="0" cy="-15" r="4" />
    </g>
  ),
  needle: (
    <g transform="rotate(30)">
      <path className="m2t-line m2t-thick" d="M0 0 L0 -64" />
      <ellipse className="m2t-body" cx="0" cy="-56" rx="1.8" ry="5" />
      <path className="m2t-line" d="M0 -56 C10 -66 18 -50 26 -60" />
    </g>
  ),
  palette: (
    <g transform="translate(-6 -10) rotate(-10)">
      <path
        className="m2t-body"
        d="M0 0 C-14 -36 26 -60 60 -48 C92 -36 92 -6 68 0 C56 4 58 -12 44 -8 C32 -4 34 8 0 0 Z"
      />
      <circle className="m2t-ink" cx="24" cy="-32" r="4" />
      <circle className="m2t-ink" cx="44" cy="-40" r="4" />
      <circle className="m2t-ink" cx="66" cy="-30" r="4" />
    </g>
  ),
};

/** The pencil, or whatever it has turned into. Keyed on the tool, so each
 *  change plays the turn (MyMaker.css, .m2t-turn). */
export function Tool({ name }) {
  return (
    <g key={name} className="m2t-turn">
      {TOOLS[name] ?? TOOLS.pencil}
    </g>
  );
}
